import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Icon, iconPath } from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  BRAZILIAN_STATE_INFO,
  type BrazilianState,
  type ManagementStage,
} from '@evogard/domain';
import type { ManagementDispatch } from '@evogard/contract';
import {
  BRAZIL_MAP_PROJECTION,
  BRAZIL_MAP_SIZE,
  BRAZIL_MAP_STATES,
} from '../../components/map/brazilStates.js';
import { SERVICE_ICON } from './assistance.js';

/**
 * O mapa da Gestão da assistência (`assistencia-35`, 30/10/2026): o desenho
 * dos estados é o do Painel do sinistro e do Financeiro
 * (`components/map/brazilStates.ts`, da malha do IBGE), sem Google — não há
 * custo por carregamento e o contorno não depende de chave.
 *
 * Duas visões. **Por estado**: o estado pinta pela quantidade e ganha uma
 * bolha com o número. **Cada serviço**: um pino por acionamento, com a ponta
 * no endereço de origem, a COR da etapa e o ÍCONE do tipo de serviço; o
 * reboque traça a linha até o destino. Clicar num estado aproxima nele e
 * passa para os pinos.
 *
 * O endereço do acionamento guarda o texto do Google e a coordenada, e não a
 * UF. O estado de cada serviço sai da coordenada, testada contra o contorno de
 * cada estado (`ufOfPoint`) — o mesmo desenho que a tela mostra, então o pino
 * nunca cai num estado e conta em outro.
 *
 * O pino não engorda com o zoom: o tamanho dele é dividido pela escala do
 * quadro, e a linha usa `non-scaling-stroke`.
 *
 * ## Abrir o estado abre os municípios
 *
 * Clicar num estado aproxima o país até ele e, quando o zoom termina, troca o
 * desenho pelo dos municípios (`components/map/cities/<IBGE>.ts`, os mesmos do
 * Painel do Financeiro), baixado no clique. O município pinta pela quantidade
 * de serviços que começaram nele, e o nome aparece onde há serviço.
 *
 * Cada arquivo de município tem a PRÓPRIA projeção — o cosseno do meio do
 * estado, e não o de 15° S do país —, e por isso o pino é projetado de novo
 * na troca (`projection`, exportada pelo gerador). Desenhar o pino com a
 * projeção do país sobre os municípios o poria quilômetros fora do lugar.
 */

const FULL: Box = [0, 0, BRAZIL_MAP_SIZE.width, BRAZIL_MAP_SIZE.height];
type Box = [number, number, number, number];

// @valor
export const STAGE_COLOR: Readonly<Record<ManagementStage, string>> = {
  nao_abriu: 'var(--color-fase-vermelho)',
  link_aberto: 'var(--color-fase-roxo)',
  a_caminho: 'var(--color-fase-azul)',
  no_local: 'var(--color-fase-ciano)',
  em_servico: 'var(--color-fase-turquesa)',
  em_transito: 'var(--color-fase-laranja)',
  no_destino: 'var(--color-fase-amarelo)',
  finalizado: 'var(--color-fase-verde)',
  agendado: 'var(--color-fase-cinza)',
  cancelado: 'var(--color-fase-cinza)',
};

const SERVICE_LABEL = Object.fromEntries(
  ASSISTANCE_SERVICES.map((s) => [s.type, s.label]),
) as Record<ManagementDispatch['serviceType'], string>;

type Projection = {
  minLng: number;
  maxLat: number;
  kx: number;
  scale: number;
  pad: number;
};
type Point = { lat: number; lng: number };

const projectWith =
  ({ minLng, maxLat, kx, scale, pad }: Projection) =>
  (p: Point): [number, number] => [
    pad + (p.lng - minLng) * kx * scale,
    pad + (maxLat - p.lat) * scale,
  ];

export const project = projectWith(BRAZIL_MAP_PROJECTION);

/** O desenho dos municípios de um estado, como `tools/geo/state-cities.mjs` gera. */
type CityShapes = {
  size: { width: number; height: number };
  projection: Projection;
  outline: string;
  cities: readonly { id: string; name: string; d: string; x: number; y: number }[];
};
type CityShape = CityShapes['cities'][number];

/* Um arquivo por estado, baixado quando o estado é aberto. */
const CITY_FILES = import.meta.glob<CityShapes>('../../components/map/cities/*.ts');

function useCityShapes(uf: BrazilianState | null) {
  return useQuery({
    queryKey: ['mapa', 'municipios-com-projecao', uf],
    staleTime: Infinity,
    enabled: uf !== null,
    queryFn: () => {
      const code = BRAZILIAN_STATE_INFO[uf!].ibge;
      const load = CITY_FILES[`../../components/map/cities/${code}.ts`];
      if (!load) throw new Error(`Não há o desenho dos municípios de ${uf}.`);
      return load();
    },
  });
}

/** O município de cada ponto, pelo contorno desenhado — como `ufOfPoint`, um nível abaixo. */
function cityOf(
  shapes: CityShapes,
  cache: Map<CityShape, Path2D>,
  at: [number, number],
): string | null {
  probe ??= document.createElement('canvas').getContext('2d');
  if (!probe) return null;
  for (const c of shapes.cities) {
    let path = cache.get(c);
    if (!path) {
      path = new Path2D(c.d);
      cache.set(c, path);
    }
    if (probe.isPointInPath(path, at[0], at[1])) return c.id;
  }
  return null;
}

/* O contorno de cada estado como `Path2D`, criado na primeira pergunta. */
let paths: { uf: BrazilianState; path: Path2D }[] | null = null;
let probe: CanvasRenderingContext2D | null = null;
const ufCache = new Map<string, BrazilianState | null>();

/** O estado em que a coordenada cai, pelo mesmo contorno que o mapa desenha. */
export function ufOfPoint(p: { lat: number; lng: number }): BrazilianState | null {
  const key = `${p.lat},${p.lng}`;
  const hit = ufCache.get(key);
  if (hit !== undefined) return hit;
  if (typeof document === 'undefined') return null;
  paths ??= BRAZIL_MAP_STATES.map((s) => ({ uf: s.uf, path: new Path2D(s.d) }));
  probe ??= document.createElement('canvas').getContext('2d');
  const [x, y] = project(p);
  const uf = probe
    ? (paths.find((s) => probe!.isPointInPath(s.path, x, y))?.uf ?? null)
    : null;
  ufCache.set(key, uf);
  return uf;
}

/* O retângulo de cada estado, lido do próprio contorno. */
const BOXES = new Map<BrazilianState, Box>(
  BRAZIL_MAP_STATES.map((s) => {
    const nums = s.d.match(/-?\d+(\.\d+)?/g)!.map(Number);
    const xs = nums.filter((_, i) => i % 2 === 0);
    const ys = nums.filter((_, i) => i % 2 === 1);
    const x0 = Math.min(...xs);
    const y0 = Math.min(...ys);
    return [s.uf, [x0, y0, Math.max(...xs) - x0, Math.max(...ys) - y0]];
  }),
);

/**
 * O quadro que mostra a caixa inteira, com folga, na proporção da ÁREA do
 * mapa — que muda na largura total e na tela cheia. Quadro e área na mesma
 * proporção é o que faz o pino ter o tamanho certo (`k`) e o mapa ocupar a
 * área toda, sem faixa vazia dos lados.
 */
function fit([x, y, w, h]: Box, pad = 0.15, ratio = FULL[3] / FULL[2]): Box {
  let fw = w * (1 + pad * 2);
  let fh = h * (1 + pad * 2);
  if (fh / fw > ratio) fw = fh / ratio;
  else fh = fw * ratio;
  return [x + w / 2 - fw / 2, y + h / 2 - fh / 2, fw, fh];
}

const reducedMotion = () =>
  typeof matchMedia !== 'undefined' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches;

/* O pino: a ponta em (0,0), a cabeça de raio 11 com o centro em (0,-18.5). */
/** O pino tem 22×30 no desenho; na tela sai 1,5× isso, 33×45 px. */
const PIN_SCALE = 1.5;
const PIN = 'M0 0C-2.6-4.6-11-10.4-11-18.5A11 11 0 1 1 11-18.5C11-10.4 2.6-4.6 0 0Z';

export type MapView = 'estados' | 'servicos';

export function ManagementMap({
  items,
  view,
  uf,
  onUf,
  selected,
  onSelect,
  detail,
  fresh,
  height,
  children,
}: {
  items: readonly ManagementDispatch[];
  view: MapView;
  uf: BrazilianState | null;
  onUf: (uf: BrazilianState | null) => void;
  selected: number | null;
  onSelect: (id: number | null) => void;
  /** O card do serviço escolhido, por cima do mapa, no canto. */
  detail?: ReactNode;
  /** Os serviços que acabaram de entrar ou mudar de etapa: o pino pulsa. */
  fresh?: ReadonlySet<number>;
  /** A altura fixa da área (largura total, tela cheia); sem ela, a proporção do país. */
  height?: string;
  /** O que flutua por cima do mapa — a legenda e os avisos —, posicionado por quem chama. */
  children?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [vb, setVbState] = useState<Box>(FULL);
  const vbRef = useRef<Box>(FULL);
  const setVb = (next: Box) => {
    vbRef.current = next;
    setVbState(next);
  };
  const [px, setPx] = useState(600);
  const ratio = useRef(FULL[3] / FULL[2]);
  const fitR = (b: Box, pad?: number) => fit(b, pad, ratio.current);
  const anim = useRef(0);
  const drag = useRef<{ x: number; y: number; vb: Box; moved: boolean } | null>(null);
  const [tip, setTip] = useState<{
    x: number;
    y: number;
    d: ManagementDispatch | null;
    uf: BrazilianState | null;
    city: { name: string; n: number } | null;
  } | null>(null);

  /* O estado aberto troca para os municípios quando o zoom chega e o desenho baixou. */
  const shapes = useCityShapes(uf);
  const [arrived, setArrived] = useState<BrazilianState | null>(null);
  const local = uf !== null && arrived === uf && shapes.data ? shapes.data : null;
  const toXY = useMemo(() => (local ? projectWith(local.projection) : project), [local]);
  const frame: Box = local ? [0, 0, local.size.width, local.size.height] : FULL;
  const pathCache = useRef(new Map<CityShape, Path2D>());

  const placed = useMemo(
    () =>
      items
        .filter((d) => d.origin !== null)
        .map((d) => ({ d, uf: ufOfPoint(d.origin!), at: toXY(d.origin!) }))
        /* entre os municípios só o estado está desenhado: o pino de fora ficaria no vazio */
        .filter((p) => !local || p.uf === uf),
    [items, toXY, local, uf],
  );
  const cityCounts = useMemo(() => {
    const m = new Map<string, number>();
    if (!local) return m;
    for (const p of placed) {
      const id = cityOf(local, pathCache.current, p.at);
      if (id) m.set(id, (m.get(id) ?? 0) + 1);
    }
    return m;
  }, [placed, local]);
  const cityMax = Math.max(1, ...cityCounts.values());
  const counts = useMemo(() => {
    const m = new Map<BrazilianState, number>();
    for (const p of placed) if (p.uf) m.set(p.uf, (m.get(p.uf) ?? 0) + 1);
    return m;
  }, [placed]);
  const max = Math.max(1, ...counts.values());
  const pins = view === 'servicos' || uf !== null;

  /* A área mudou de forma (largura total, tela cheia): reenquadra o que estava à vista. */
  const home = useRef<() => Box>(() => FULL);
  home.current = () =>
    local ? fitR(frame) : uf ? fitR(BOXES.get(uf)!) : fitR(FULL, 0.02);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth || 600;
      const h = el.clientHeight || w;
      setPx(w);
      if (Math.abs(h / w - ratio.current) > 0.01) {
        ratio.current = h / w;
        cancelAnimationFrame(anim.current);
        setVb(home.current());
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const flyTo = (target: Box, done?: () => void) => {
    cancelAnimationFrame(anim.current);
    const from = vbRef.current;
    if (reducedMotion()) {
      setVb(target);
      done?.();
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 420);
      const e = 1 - (1 - k) ** 3;
      setVb(from.map((v, i) => v + (target[i]! - v) * e) as Box);
      if (k < 1) anim.current = requestAnimationFrame(step);
      else done?.();
    };
    anim.current = requestAnimationFrame(step);
  };

  /*
   * Abrir um estado: o país aproxima até ele, e só então os municípios entram.
   * Voltar: o país nasce enquadrado no estado de onde se saiu e se afasta.
   */
  const left = useRef<BrazilianState | null>(null);
  useEffect(() => {
    if (uf) {
      left.current = uf;
      flyTo(fitR(BOXES.get(uf)!), () => setArrived(uf));
      return;
    }
    setArrived(null);
    if (left.current) setVb(fitR(BOXES.get(left.current)!));
    left.current = null;
    flyTo(fitR(FULL, 0.02));
  }, [uf]);

  /* A troca para os municípios começa no mesmo enquadramento em que o país parou. */
  useLayoutEffect(() => {
    if (local) setVb(fitR([0, 0, local.size.width, local.size.height]));
  }, [local]);

  /* O serviço escolhido na lista: aproxima na origem e no destino dele. */
  useEffect(() => {
    const d = items.find((i) => i.id === selected);
    if (!d?.origin) return;
    const pts = [toXY(d.origin), ...(d.destination ? [toXY(d.destination)] : [])];
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const r = 3;
    flyTo(
      fitR(
        [
          Math.min(...xs) - r,
          Math.min(...ys) - r,
          Math.max(...xs) - Math.min(...xs) + 2 * r,
          Math.max(...ys) - Math.min(...ys) + 2 * r,
        ],
        0.4,
      ),
    );
  }, [selected, local]);

  const zoom = (f: number, cx = vb[0] + vb[2] / 2, cy = vb[1] + vb[3] / 2) => {
    cancelAnimationFrame(anim.current);
    const w = Math.min(frame[2] * 1.3, Math.max(frame[2] / 150, vb[2] * f));
    const h = (w * vb[3]) / vb[2];
    setVb([cx - ((cx - vb[0]) * w) / vb[2], cy - ((cy - vb[1]) * h) / vb[3], w, h]);
  };

  /* A roda aproxima em volta do ponteiro; o ouvinte não é passivo para segurar a página. */
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const cx = vb[0] + ((e.clientX - r.left) / r.width) * vb[2];
      const cy = vb[1] + ((e.clientY - r.top) / r.height) * vb[3];
      zoom(e.deltaY > 0 ? 1.2 : 1 / 1.2, cx, cy);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const k = vb[2] / px;

  const down = (e: PointerEvent<SVGSVGElement>) => {
    drag.current = { x: e.clientX, y: e.clientY, vb, moved: false };
  };
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const g = drag.current;
    if (g) {
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (!g.moved && Math.hypot(dx, dy) < 4) return;
      if (!g.moved) e.currentTarget.setPointerCapture(e.pointerId);
      g.moved = true;
      setTip(null);
      cancelAnimationFrame(anim.current);
      const s = g.vb[2] / px;
      setVb([g.vb[0] - dx * s, g.vb[1] - dy * s, g.vb[2], g.vb[3]]);
      return;
    }
    const target = e.target as Element;
    const r = box.current!.getBoundingClientRect();
    const pin = target.closest('[data-id]');
    const state = target.closest('[data-uf]');
    const city = target.closest('[data-city]');
    const at = { x: e.clientX - r.left, y: e.clientY - r.top };
    if (pin) {
      const d = items.find((i) => i.id === Number(pin.getAttribute('data-id'))) ?? null;
      setTip({ ...at, d, uf: null, city: null });
    } else if (city && local) {
      const id = city.getAttribute('data-city')!;
      const name = local.cities.find((c) => c.id === id)?.name ?? '';
      setTip({ ...at, d: null, uf: null, city: { name, n: cityCounts.get(id) ?? 0 } });
    } else if (state) {
      setTip({
        ...at,
        d: null,
        uf: state.getAttribute('data-uf') as BrazilianState,
        city: null,
      });
    } else setTip(null);
  };
  const up = (e: PointerEvent<SVGSVGElement>) => {
    const g = drag.current;
    drag.current = null;
    if (!g || g.moved) return;
    const target = e.target as Element;
    const pin = target.closest('[data-id]');
    if (pin) {
      const id = Number(pin.getAttribute('data-id'));
      onSelect(selected === id ? null : id);
      return;
    }
    const state = target.closest('[data-uf]')?.getAttribute('data-uf') as
      BrazilianState | undefined;
    if (state && state !== uf) onUf(state);
  };

  /* O aberto por cima; o escolhido por cima de todos. */
  const ordered = useMemo(
    () =>
      [...placed].sort(
        (a, b) =>
          Number(a.d.id === selected) - Number(b.d.id === selected) ||
          Number(a.d.stage !== 'finalizado' && a.d.stage !== 'cancelado') -
            Number(b.d.stage !== 'finalizado' && b.d.stage !== 'cancelado') ||
          a.at[1] - b.at[1],
      ),
    [placed, selected],
  );
  const dense = placed.length > 160 && vb[2] > frame[2] * 0.3;

  return (
    <div
      ref={box}
      className="relative w-full overflow-hidden border-y border-borda bg-superficie-suave"
      style={
        height
          ? { height }
          : { aspectRatio: `${FULL[2]} / ${FULL[3]}`, maxHeight: '42rem' }
      }
    >
      <svg
        ref={svg}
        viewBox={vb.join(' ')}
        className="block h-full w-full cursor-grab touch-none select-none active:cursor-grabbing"
        role="img"
        aria-label={`Mapa ${uf ? `de ${BRAZILIAN_STATE_INFO[uf].name}` : 'do Brasil'} com ${placed.length} acionamentos`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={() => setTip(null)}
      >
        {local && (
          <g>
            {local.cities.map((c) => {
              const n = cityCounts.get(c.id) ?? 0;
              return (
                <path
                  key={c.id}
                  d={c.d}
                  data-city={c.id}
                  /* a divisa entre municípios é linha de leitura, não textura: borda forte */
                  className={
                    n > 0
                      ? 'fill-primaria stroke-borda-forte hover:stroke-texto'
                      : 'fill-superficie stroke-borda-forte hover:stroke-texto'
                  }
                  style={{
                    fillOpacity: n > 0 ? 0.1 + 0.35 * (n / cityMax) : 1,
                    strokeOpacity: 0.8,
                    strokeWidth: 0.9,
                    vectorEffect: 'non-scaling-stroke',
                  }}
                />
              );
            })}
            <path
              d={local.outline}
              fill="none"
              className="pointer-events-none stroke-texto-suave"
              style={{ strokeWidth: 1.4, vectorEffect: 'non-scaling-stroke' }}
            />
            {local.cities
              .filter((c) => cityCounts.has(c.id))
              .map((c) => (
                <text
                  key={c.id}
                  x={c.x}
                  y={c.y}
                  dy={14 * k}
                  textAnchor="middle"
                  className="pointer-events-none fill-texto-suave font-semibold"
                  style={{ fontSize: 10.5 * k }}
                >
                  {c.name}
                </text>
              ))}
          </g>
        )}
        <g display={local ? 'none' : undefined}>
          {BRAZIL_MAP_STATES.map((s) => {
            const n = counts.get(s.uf) ?? 0;
            return (
              <path
                key={s.uf}
                d={s.d}
                data-uf={s.uf}
                className={
                  n > 0 && !pins
                    ? 'cursor-pointer fill-primaria stroke-borda-forte hover:stroke-texto'
                    : 'cursor-pointer fill-superficie stroke-borda-forte hover:stroke-texto'
                }
                style={{
                  fillOpacity: n > 0 && !pins ? 0.12 + 0.5 * (n / max) : 1,
                  strokeOpacity: s.uf === uf ? 1 : 0.5,
                  strokeWidth: s.uf === uf ? 1.6 : 0.8,
                  vectorEffect: 'non-scaling-stroke',
                }}
              />
            );
          })}
        </g>
        {pins &&
          !local &&
          BRAZIL_MAP_STATES.filter(
            (s) => s.uf === uf || BOXES.get(s.uf)![2] > 40 * k,
          ).map((s) => (
            <text
              key={s.uf}
              x={s.x}
              y={s.y}
              textAnchor="middle"
              className="pointer-events-none fill-texto-tenue font-semibold"
              style={{ fontSize: 10 * k }}
            >
              {s.uf}
            </text>
          ))}
        {!pins &&
          [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([state, n]) => {
              const s = BRAZIL_MAP_STATES.find((x) => x.uf === state)!;
              const r = (11 + Math.sqrt(n / max) * 20) * k;
              return (
                <g key={state} data-uf={state} className="cursor-pointer">
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={r}
                    className="fill-primaria stroke-superficie"
                    style={{ strokeWidth: 2, vectorEffect: 'non-scaling-stroke' }}
                  />
                  <text
                    x={s.x}
                    y={s.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="pointer-events-none fill-primaria-contraste font-bold tabular-nums"
                    style={{ fontSize: (n > 99 ? 11 : 12.5) * k }}
                  >
                    {n}
                  </text>
                </g>
              );
            })}
        {pins &&
          ordered.map(({ d, at: [x, y] }) => {
            const color = STAGE_COLOR[d.stage];
            const sel = d.id === selected;
            const faded = selected !== null && !sel;
            const s = k * PIN_SCALE * (dense ? 0.62 : 1) * (sel ? 1.25 : 1);
            const live = d.onStreet && !dense;
            const dest = d.destination ? toXY(d.destination) : null;
            const done = d.stage === 'no_destino' || d.stage === 'finalizado';
            return (
              <g
                key={d.id}
                data-id={d.id}
                className="cursor-pointer"
                style={{ opacity: faded ? 0.2 : d.stage === 'cancelado' ? 0.6 : 1 }}
              >
                {dest && (
                  <>
                    <Envelope
                      from={[x, y]}
                      to={dest}
                      pad={(dense ? 12 : 22) * k}
                      color={color}
                      selected={sel}
                    />
                    <path
                      d={`M${x} ${y}Q${(x + dest[0]) / 2 - (dest[1] - y) * 0.18} ${(y + dest[1]) / 2 + (dest[0] - x) * 0.18} ${dest[0]} ${dest[1]}`}
                      fill="none"
                      stroke={color}
                      strokeWidth={dense ? 1 : 1.8}
                      strokeDasharray={done ? undefined : '4 4'}
                      strokeLinecap="round"
                      style={{ vectorEffect: 'non-scaling-stroke' }}
                    />
                    <rect
                      x={dest[0] - 3.6 * k}
                      y={dest[1] - 3.6 * k}
                      width={7.2 * k}
                      height={7.2 * k}
                      rx={1.4 * k}
                      stroke={color}
                      className="fill-superficie"
                      style={{ strokeWidth: 1.6, vectorEffect: 'non-scaling-stroke' }}
                    />
                  </>
                )}
                <ellipse
                  cx={x}
                  cy={y}
                  rx={(dense ? 7 : 13) * k}
                  ry={(dense ? 7 : 13) * k * 0.45}
                  fill={color}
                  fillOpacity={0.18}
                  stroke={color}
                  strokeOpacity={0.55}
                  className={live ? 'motion-safe:animate-pulse' : undefined}
                  style={{ vectorEffect: 'non-scaling-stroke' }}
                />
                <g transform={`translate(${x} ${y}) scale(${s})`}>
                  {fresh?.has(d.id) && (
                    <circle
                      cx={0}
                      cy={-18.5}
                      r={15}
                      fill={color}
                      fillOpacity={0.5}
                      className="motion-safe:animate-ping"
                      style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
                    />
                  )}
                  <path
                    d={PIN}
                    fill={color}
                    className={sel ? 'stroke-texto' : 'stroke-superficie'}
                    strokeWidth={sel ? 2 : 1.6}
                  />
                  <svg
                    x={-7.5}
                    y={-26}
                    width={15}
                    height={15}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d={iconPath(SERVICE_ICON[d.serviceType])}
                      stroke="white"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </g>
              </g>
            );
          })}
      </svg>

      <div className="absolute top-3 right-3 flex flex-col gap-1">
        <Button
          variant="secundario"
          size="sm"
          aria-label="Aproximar"
          onClick={() => zoom(1 / 1.5)}
        >
          <Icon nome="mais" size={16} />
        </Button>
        <Button
          variant="secundario"
          size="sm"
          aria-label="Afastar"
          onClick={() => zoom(1.5)}
        >
          <Icon nome="menos" size={16} />
        </Button>
        <Button
          variant="secundario"
          size="sm"
          aria-label="Enquadrar"
          onClick={() => flyTo(home.current())}
        >
          <Icon nome="mapa" size={16} />
        </Button>
      </div>

      {children}

      {detail && (
        <div className="absolute top-3 left-3 z-20 w-[min(22rem,calc(100%-4.5rem))]">
          {detail}
        </div>
      )}

      {tip && (tip.d || tip.uf || tip.city) && tip.d?.id !== selected && (
        <div
          className="pointer-events-none absolute z-10 w-60 rounded-borda border border-borda bg-superficie p-2.5 text-xs shadow-lg"
          style={{
            left: Math.min(tip.x + 14, (box.current?.clientWidth ?? 600) - 250),
            top: Math.min(tip.y + 14, (box.current?.clientHeight ?? 600) - 110),
          }}
        >
          {tip.d ? (
            <>
              <b className="block text-sm text-texto">
                {SERVICE_LABEL[tip.d.serviceType]} · {STAGE_LABEL[tip.d.stage]}
              </b>
              <span className="block text-texto-suave">
                {tip.d.code}
                {tip.d.plate ? ` · placa ${tip.d.plate}` : ''}
              </span>
              {tip.d.originLabel && (
                <span className="block text-texto-suave">{tip.d.originLabel}</span>
              )}
              {tip.d.providerName && (
                <span className="block text-texto-suave">{tip.d.providerName}</span>
              )}
            </>
          ) : tip.city ? (
            <>
              <b className="block text-sm text-texto">{tip.city.name}</b>
              <span className="block text-texto-suave">
                {tip.city.n === 1 ? '1 acionamento' : `${tip.city.n} acionamentos`}
              </span>
            </>
          ) : (
            <>
              <b className="block text-sm text-texto">
                {BRAZILIAN_STATE_INFO[tip.uf!].name}
              </b>
              <span className="block text-texto-suave">
                {counts.get(tip.uf!) ?? 0} acionamentos
                {tip.uf !== uf ? ' · clique para abrir o estado' : ''}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O contorno que diz "estes dois pontos são um serviço só": uma elipse
 * inclinada no eixo origem → destino, cobrindo os dois pontos e a curva da
 * rota. Elipse e não círculo — num reboque de 40 km o círculo cobriria a
 * cidade, e os serviços vizinhos pareceriam dentro dele.
 *
 * A curva é a do `Q` com o ponto de controle a 0,18 do comprimento, na
 * perpendicular; no meio ela se afasta 0,09 do eixo, sempre para o mesmo lado.
 * Por isso o centro anda metade disso para o lado da curva, e o raio menor
 * cobre a outra metade mais a folga.
 *
 * Não recebe clique: num serviço longo ela cobre estados e outros pinos, e
 * quem escolhe o serviço é o pino, a linha ou a fila.
 */
function Envelope({
  from: [x0, y0],
  to: [x1, y1],
  pad,
  color,
  selected,
}: {
  from: [number, number];
  to: [number, number];
  pad: number;
  color: string;
  selected: boolean;
}) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  const cx = (x0 + x1) / 2 - dy * 0.045;
  const cy = (y0 + y1) / 2 + dx * 0.045;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <ellipse
      cx={cx}
      cy={cy}
      rx={len / 2 + pad}
      ry={len * 0.045 + pad}
      transform={`rotate(${angle} ${cx} ${cy})`}
      fill={color}
      fillOpacity={selected ? 0.12 : 0.06}
      stroke={color}
      strokeOpacity={selected ? 0.9 : 0.45}
      strokeWidth={selected ? 1.6 : 1.2}
      strokeDasharray="2 3"
      pointerEvents="none"
      style={{ vectorEffect: 'non-scaling-stroke' }}
    />
  );
}

// @valor
export const STAGE_LABEL: Readonly<Record<ManagementStage, string>> = {
  nao_abriu: 'Não abriu o link',
  link_aberto: 'Link aberto',
  a_caminho: 'A caminho',
  no_local: 'No local',
  em_servico: 'Em serviço',
  em_transito: 'Em trânsito',
  no_destino: 'No destino',
  finalizado: 'Finalizado',
  agendado: 'Agendado',
  cancelado: 'Cancelado',
};
